/**
 * Documento del tablero: figuras + metadatos sobre Yjs, con deshacer/rehacer local.
 * Toda escritura pasa por aquí para que quede en una sola transacción y en el historial.
 */
import * as Y from 'yjs'
import type { BackgroundId, BoardKind, MapLayoutId, Shape, ShapeId } from '../../shared/types.ts'

export const LOCAL_ORIGIN = 'local-user'

/**
 * La geometría derivada de un tema del mapa mental (posición, tamaño, campos
 * calculados). Vive en su PROPIO mapa Yjs, separado de la figura: así el motor
 * de layout jamás reescribe la clave de la figura y no puede resucitar temas
 * borrados por otro cliente ni pisarle el texto o el pliegue a nadie.
 */
export interface TopicLayout {
  x: number
  y: number
  w: number
  h: number
  depth: number
  branchColor: string
  hidden?: boolean
  hiddenCount?: number
  side?: 'right' | 'left' | 'down'
  /**
   * Con qué texto y nivel se midió w/h. Cada plataforma mide con SU fuente
   * (Segoe en Windows, San Francisco en Mac/iPad) y el mismo texto da anchos
   * distintos: si cada cliente volvía a medir, se corregían mutuamente para
   * siempre. Mientras texto y nivel no cambien, la medida guardada manda.
   */
  mText?: string
  mDepth?: number
}

const TOPIC_LAYOUT_KEYS = [
  'x',
  'y',
  'w',
  'h',
  'depth',
  'branchColor',
  'hidden',
  'hiddenCount',
  'side',
  'mText',
  'mDepth',
] as const

export class BoardDoc {
  readonly doc: Y.Doc
  readonly shapes: Y.Map<Shape>
  readonly meta: Y.Map<unknown>
  /** geometría derivada de los temas del mapa, por id (ver TopicLayout) */
  readonly layouts: Y.Map<TopicLayout>
  readonly undoManager: Y.UndoManager
  private listeners = new Set<(ids: Set<ShapeId>) => void>()
  private metaListeners = new Set<() => void>()

  constructor(doc: Y.Doc) {
    this.doc = doc
    this.shapes = doc.getMap<Shape>('shapes')
    this.meta = doc.getMap('meta')
    this.layouts = doc.getMap<TopicLayout>('topicLayout')
    this.undoManager = new Y.UndoManager([this.shapes, this.meta, this.layouts], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 400,
    })

    this.shapes.observe((event) => {
      const changed = new Set<ShapeId>(event.keysChanged as Set<string>)
      for (const cb of this.listeners) cb(changed)
    })
    this.layouts.observe((event) => {
      const changed = new Set<ShapeId>(event.keysChanged as Set<string>)
      for (const cb of this.listeners) cb(changed)
    })
    this.meta.observe(() => {
      for (const cb of this.metaListeners) cb()
    })
  }

  /** Figura con su geometría derivada encima (los temas se leen SIEMPRE así). */
  private merged(shape: Shape): Shape {
    if (shape.type !== 'topic') return shape
    const layout = this.layouts.get(shape.id)
    if (!layout) return shape
    return { ...shape, ...layout }
  }

  onChange(cb: (ids: Set<ShapeId>) => void): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  onMetaChange(cb: () => void): () => void {
    this.metaListeners.add(cb)
    return () => this.metaListeners.delete(cb)
  }

  transact(fn: () => void): void {
    this.doc.transact(fn, LOCAL_ORIGIN)
  }

  get(id: ShapeId): Shape | undefined {
    const shape = this.shapes.get(id)
    return shape ? this.merged(shape) : undefined
  }

  all(): Shape[] {
    return Array.from(this.shapes.values(), (s) => this.merged(s)).sort((a, b) => a.z - b.z)
  }

  count(): number {
    return this.shapes.size
  }

  /**
   * Un tema se escribe partido en dos: lo estructural (texto, padre, orden,
   * pliegue) a `shapes` y la geometría derivada a `layouts`. Quien mueve o
   * edita escribe los dos; el motor de layout escribe SOLO `layouts`.
   */
  private setSplit(shape: Shape): void {
    if (shape.type !== 'topic') {
      this.shapes.set(shape.id, shape)
      return
    }
    const layout: Record<string, unknown> = {}
    for (const key of TOPIC_LAYOUT_KEYS) {
      const value = (shape as unknown as Record<string, unknown>)[key]
      if (value !== undefined) layout[key] = value
    }
    this.shapes.set(shape.id, shape)
    this.layouts.set(shape.id, layout as unknown as TopicLayout)
  }

  put(shape: Shape): void {
    this.transact(() => this.setSplit(shape))
  }

  putMany(shapes: Shape[]): void {
    this.transact(() => {
      for (const s of shapes) this.setSplit(s)
    })
  }

  /** Escritura sin historial (p. ej. mover con arrastre en vivo: el historial se marca al soltar). */
  putSilent(shapes: Shape[]): void {
    this.doc.transact(() => {
      for (const s of shapes) this.setSplit(s)
    }, 'silent')
  }

  update<T extends Shape>(id: ShapeId, patch: Partial<T>): void {
    const current = this.shapes.get(id)
    if (!current) return
    this.put({ ...current, ...patch, updatedAt: Date.now() } as Shape)
  }

  delete(ids: ShapeId[]): void {
    this.transact(() => {
      for (const id of ids) {
        this.shapes.delete(id)
        if (this.layouts.has(id)) this.layouts.delete(id)
      }
    })
  }

  maxZ(): number {
    let max = 0
    for (const s of this.shapes.values()) if (s.z > max) max = s.z
    return max
  }

  minZ(): number {
    let min = Number.MAX_SAFE_INTEGER
    for (const s of this.shapes.values()) if (s.z < min) min = s.z
    return min === Number.MAX_SAFE_INTEGER ? 0 : min
  }

  bringToFront(ids: ShapeId[]): void {
    let z = this.maxZ()
    this.transact(() => {
      for (const id of ids) {
        const s = this.shapes.get(id)
        if (s) this.shapes.set(id, { ...s, z: ++z })
      }
    })
  }

  sendToBack(ids: ShapeId[]): void {
    let z = this.minZ()
    this.transact(() => {
      for (const id of [...ids].reverse()) {
        const s = this.shapes.get(id)
        if (s) this.shapes.set(id, { ...s, z: --z })
      }
    })
  }

  get name(): string {
    return (this.meta.get('name') as string) ?? 'Pizarra sin título'
  }

  setName(name: string): void {
    this.transact(() => this.meta.set('name', name))
  }

  get background(): BackgroundId {
    return (this.meta.get('background') as BackgroundId) ?? 'dots'
  }

  /** Pizarra libre o mapa mental. */
  get kind(): BoardKind {
    return (this.meta.get('kind') as BoardKind) ?? 'board'
  }

  setKind(kind: BoardKind): void {
    this.transact(() => this.meta.set('kind', kind))
  }

  /** Estructura del mapa mental (balanceado, lógico, organigrama). */
  get mapLayout(): MapLayoutId {
    return (this.meta.get('mapLayout') as MapLayoutId) ?? 'map'
  }

  setMapLayout(layout: MapLayoutId): void {
    this.transact(() => this.meta.set('mapLayout', layout))
  }

  setBackground(bg: BackgroundId): void {
    this.transact(() => this.meta.set('background', bg))
  }

  undo(): void {
    this.undoManager.undo()
  }

  redo(): void {
    this.undoManager.redo()
  }

  canUndo(): boolean {
    return this.undoManager.canUndo()
  }

  canRedo(): boolean {
    return this.undoManager.canRedo()
  }
}
